import firebase, { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";

export type CommunityReelCreatorConfig = {
  reelVisualStyle?: string;
  reelVisualStyleReferenceImage?: string;
  useThreeStepValidator?: boolean;
  reelFormatPromptTemplates?: Record<string, string>;
  threeStepValidatorPromptTemplates?: Record<string, string>;
};

const REEL_CREATOR_CONFIG_DOC_ID = "settings";

const LEGACY_REEL_CREATOR_FIELDS = [
  "reelVisualStyle",
  "reelVisualStyleReferenceImage",
  "useThreeStepValidator",
  "reelFormatPromptTemplates",
  "threeStepValidatorPromptTemplates",
] as const;

type LegacyReelCreatorField = (typeof LEGACY_REEL_CREATOR_FIELDS)[number];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const normalizeStringMap = (value: unknown): Record<string, string> | undefined => {
  if (!isRecord(value)) return undefined;

  const normalized: Record<string, string> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (typeof entryValue === "string") {
      normalized[key] = entryValue;
    }
  });

  return normalized;
};

const normalizeCommunityReelCreatorConfig = (value: unknown): CommunityReelCreatorConfig => {
  if (!isRecord(value)) return {};

  const normalized: CommunityReelCreatorConfig = {};

  if (typeof value.reelVisualStyle === "string") {
    normalized.reelVisualStyle = value.reelVisualStyle;
  }

  if (typeof value.reelVisualStyleReferenceImage === "string") {
    normalized.reelVisualStyleReferenceImage = value.reelVisualStyleReferenceImage;
  }

  if (typeof value.useThreeStepValidator === "boolean") {
    normalized.useThreeStepValidator = value.useThreeStepValidator;
  }

  const reelFormatPromptTemplates = normalizeStringMap(value.reelFormatPromptTemplates);
  if (reelFormatPromptTemplates !== undefined) {
    normalized.reelFormatPromptTemplates = reelFormatPromptTemplates;
  }

  const threeStepValidatorPromptTemplates = normalizeStringMap(value.threeStepValidatorPromptTemplates);
  if (threeStepValidatorPromptTemplates !== undefined) {
    normalized.threeStepValidatorPromptTemplates = threeStepValidatorPromptTemplates;
  }

  return normalized;
};

const extractLegacyCommunityReelCreatorConfig = (communityData: unknown): CommunityReelCreatorConfig => {
  if (!isRecord(communityData)) return {};

  const legacyPayload: Partial<Record<LegacyReelCreatorField, unknown>> = {};
  LEGACY_REEL_CREATOR_FIELDS.forEach((field) => {
    if (hasOwn(communityData, field)) {
      legacyPayload[field] = communityData[field];
    }
  });

  return normalizeCommunityReelCreatorConfig(legacyPayload);
};

const buildLegacyReelCreatorDeletePayload = (): Record<LegacyReelCreatorField, firebase.firestore.FieldValue> =>
  LEGACY_REEL_CREATOR_FIELDS.reduce((payload, field) => {
    payload[field] = firebase.firestore.FieldValue.delete();
    return payload;
  }, {} as Record<LegacyReelCreatorField, firebase.firestore.FieldValue>);

const mergeMissingLegacyFields = (
  legacyConfig: CommunityReelCreatorConfig,
  currentConfig: CommunityReelCreatorConfig
): CommunityReelCreatorConfig => {
  const nextConfig: CommunityReelCreatorConfig = {};

  (Object.keys(legacyConfig) as Array<keyof CommunityReelCreatorConfig>).forEach((key) => {
    if (legacyConfig[key] !== undefined && currentConfig[key] === undefined) {
      (nextConfig as Record<string, unknown>)[key] = legacyConfig[key];
    }
  });

  return nextConfig;
};

const migrateLegacyCommunityReelCreatorConfigFromData = async (
  communityId: string,
  communityData: unknown,
  currentConfig: CommunityReelCreatorConfig = {}
): Promise<CommunityReelCreatorConfig> => {
  if (!hasLegacyCommunityReelCreatorConfig(communityData)) {
    return currentConfig;
  }

  const legacyConfig = extractLegacyCommunityReelCreatorConfig(communityData);
  const missingFields = mergeMissingLegacyFields(legacyConfig, currentConfig);

  const batch = firestore.batch();
  if (Object.keys(missingFields).length) {
    batch.set(getCommunityReelCreatorConfigRef(communityId), missingFields, { merge: true });
  }
  batch.set(
    firestore.collection(DB_PATH.COMMUNITY).doc(communityId),
    buildLegacyReelCreatorDeletePayload(),
    { merge: true }
  );
  await batch.commit();

  return { ...legacyConfig, ...currentConfig };
};

export const hasLegacyCommunityReelCreatorConfig = (communityData: unknown): boolean =>
  isRecord(communityData) &&
  LEGACY_REEL_CREATOR_FIELDS.some((field) => hasOwn(communityData, field));

export const stripLegacyCommunityReelCreatorConfig = <T extends Record<string, unknown>>(
  communityData: T
): Omit<T, LegacyReelCreatorField> => {
  const sanitized = { ...communityData };
  LEGACY_REEL_CREATOR_FIELDS.forEach((field) => {
    delete sanitized[field];
  });
  return sanitized;
};

export const getCommunityReelCreatorConfigRef = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.REEL_CREATOR_CONFIG)
    .doc(REEL_CREATOR_CONFIG_DOC_ID);

export const getCommunityReelCreatorConfig = async (
  communityId: string,
  options?: { communityData?: unknown }
): Promise<CommunityReelCreatorConfig> => {
  if (!communityId) return {};

  const configRef = getCommunityReelCreatorConfigRef(communityId);
  const communityRef = firestore.collection(DB_PATH.COMMUNITY).doc(communityId);

  const [configSnapshot, communitySnapshot] = await Promise.all([
    configRef.get(),
    options?.communityData === undefined ? communityRef.get() : Promise.resolve(null),
  ]);

  const currentConfig = normalizeCommunityReelCreatorConfig(configSnapshot.data());
  const communityData = options?.communityData ?? communitySnapshot?.data();

  if (!hasLegacyCommunityReelCreatorConfig(communityData)) {
    return currentConfig;
  }

  return migrateLegacyCommunityReelCreatorConfigFromData(communityId, communityData, currentConfig);
};

export const saveCommunityReelCreatorConfig = async (
  communityId: string,
  config: CommunityReelCreatorConfig
): Promise<void> => {
  if (!communityId) return;

  const normalizedConfig = normalizeCommunityReelCreatorConfig(config);
  const batch = firestore.batch();
  batch.set(getCommunityReelCreatorConfigRef(communityId), normalizedConfig, { merge: true });
  batch.set(
    firestore.collection(DB_PATH.COMMUNITY).doc(communityId),
    buildLegacyReelCreatorDeletePayload(),
    { merge: true }
  );
  await batch.commit();
};

export const migrateLegacyCommunityReelCreatorConfig = async (
  communityId: string,
  communityData?: unknown
): Promise<CommunityReelCreatorConfig> => {
  if (!communityId) return {};

  const communitySnapshot =
    communityData === undefined
      ? await firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get()
      : null;
  const currentConfig = await getCommunityReelCreatorConfigRef(communityId).get();

  return migrateLegacyCommunityReelCreatorConfigFromData(
    communityId,
    communityData ?? communitySnapshot?.data(),
    normalizeCommunityReelCreatorConfig(currentConfig.data())
  );
};
